import { Box, Button, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { ContentCopyIcon, FileIcon } from 'assets/icons/v2';
import { PageContext } from 'components/common/PageContext';
import { WorksheetPreview } from 'components/worksheet/WorksheetPreview';
import { NovaIcon } from 'components/ui/novaIcon';
import { useGetWorksheetTemplate } from 'hooks/api/backend/worksheets/useWorksheetTemplates';
import { useModalService } from 'hooks/useModalService';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { ReactElement, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ModalVariant } from 'types/Modal';
import { Breadcrumb } from 'types/breadcrumbs';
import { ProtectedRoutes } from 'types/Routes';
import { getWorksheetDashboardBreadcrumbPath } from 'utils/worksheetDashboardReturnParams';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import storage from 'utils/storage';
import { schema } from './Worksheet';

export const WorksheetTemplatePreview = (): ReactElement => {
  const { id: templateId } = useParams<{ id: string }>();
  const b2bSchoolId = storage.getTenant();
  const modalService = useModalService();
  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();

  const { data: template, isLoading, isError, error, refetch } = useGetWorksheetTemplate({
    templateId,
  });

  useEffect(() => {
    if (!templateId) return undefined;

    const breadcrumbs: Breadcrumb[] = [
      {
        path: getWorksheetDashboardBreadcrumbPath(),
        name: 'Arbeitsblatt-Verwaltung',
      },
      {
        path: `${ProtectedRoutes.WORKSHEET_TEMPLATE_PREVIEW}/${templateId}`,
        name: template?.title ?? 'Vorlagenvorschau',
      },
    ];

    addBreadcrumbs(breadcrumbs);
    return () => removeBreadcrumbs();
  }, [addBreadcrumbs, removeBreadcrumbs, template?.title, templateId]);

  const handleCopy = (): void => {
    if (!templateId || !template) return;

    modalService.show(ModalVariant.WORKSHEET_TEMPLATE_COPY, {
      templateId,
      initialTitle: template.title,
      onCopied: () => undefined,
    });
  };

  if (isLoading) {
    return (
      <PageContext
        hasBackButton
        title="Arbeitsblatt-Erstellung"
        headTitle="Vorlagenvorschau"
      >
        <PageSpinner />
      </PageContext>
    );
  }

  if (isError) {
    return (
      <PageContext
        hasBackButton
        title="Arbeitsblatt-Erstellung"
        headTitle="Vorlagenvorschau"
      >
        <QueryErrorState error={error} onRetry={() => refetch()} showBack={false} />
      </PageContext>
    );
  }

  return (
    <PageContext
      hasBackButton
      title="Arbeitsblatt-Erstellung"
      headTitle={template?.title || 'Vorlagenvorschau'}
    >
      <Box my="40px" p="4xl" bg="background.primary" borderRadius={{ md: '2xl' }}>
        {template && (
          <VStack align="stretch" gap="l">
            <HStack justify="space-between" align="center">
              <HStack gap="10px">
                <Box
                  boxSize="34px"
                  bgColor="tag.background.green"
                  display="grid"
                  placeItems="center"
                >
                  <NovaIcon icon={FileIcon} size="l" color="icon.accent" aria-hidden />
                </Box>
                <Text textStyle="h2.medium" color="text.primary">
                  {template.title}
                </Text>
              </HStack>
              <Button variant="secondary" onClick={handleCopy} disabled={!b2bSchoolId}>
                <NovaIcon icon={ContentCopyIcon} size="m" fill="none" aria-hidden />
                Kopieren
              </Button>
            </HStack>

            {template.description && (
              <Text textStyle="body.m.regular" color="text.secondary">
                {template.description}
              </Text>
            )}

            <Separator />
            <WorksheetPreview<typeof schema.blockSchema>
              originDocument={template.content}
              schema={schema}
              showResetButton={false}
            />
          </VStack>
        )}
      </Box>
    </PageContext>
  );
};
